// Loading, empty and failed — TODO T17 (30 Aug 2026).
//
// A polling brain spends real time in all three, so they are components rather than an
// afterthought per screen. The failure state names what the API said, because "something went
// wrong" in a recruiter's screenshot costs a debugging session.
import type { ReactNode } from 'react';
import { ApiError } from '../api/core.ts';

export function Loading({ what }: { what: string }) {
  return <div className="state">Loading {what}…</div>;
}

/**
 * `ReactNode` since T38 (3 Sep 2026), not `string`: an empty state is often the best place to put
 * the action that fills it — "No candidates entered yet. Add the first one." — and a sentence
 * with a link in it is a node.
 */
export function Empty({ children }: { children: ReactNode }) {
  // `state--empty` draws the small radar beside the sentence (styles/app.css, T47): nothing on
  // the radar, said without changing a word of any screen's copy. The sentence is one span, so a
  // link inside it stays inline with its words rather than becoming a flex item of its own.
  return (
    <div className="state state--empty">
      <span>{children}</span>
    </div>
  );
}

const EXPLANATIONS: Record<string, string> = {
  network: 'The brain API did not answer. Is it running?',
  not_found: 'That is not in this tenant.',
  invalid_id: 'That link does not name a valid record.',
  invalid_cursor: 'The thread lost its place. Reload to start it again.',
  internal: 'The brain API failed. The detail is in its log, not here.',
  unreadable: 'The brain API answered with something this app could not read.',
  unauthenticated: 'The session expired. Sign in again.',
  bad_request: 'The brain asked for something the API refused.',
  find_unavailable: 'Finding candidates is not set up on this server.',
  find_failed: 'kadmon’s answer could not be used. Ask again.',
  rate_limited: 'Too many questions in a few minutes. Wait, then ask again.',
};

export function Failed({ what, error }: { what: string; error: unknown }) {
  // A code with no sentence here is shown as itself: a recruiter's screenshot of an unfamiliar
  // word still names the thing to look for, where "something went wrong" costs a debugging session.
  const code = error instanceof ApiError ? error.code : null;
  return (
    <div className="state state--error">
      Could not load {what}.{code && <> {EXPLANATIONS[code] ?? code}</>}
    </div>
  );
}
